import { Link } from "react-router-dom";
import { Shield, Users, Heart, Target, Mail, Award } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const TEAM_MEMBERS = [
  {
    name: "Das Team",
    role: "Ehrenamtliche Helfer",
    description: "Wir sind ein kleines Team aus Menschen, die sich gegen Mobbing einsetzen. Alle arbeiten ehrenamtlich, weil uns wichtig ist, dass Kinder und Jugendliche Hilfe bekommen.",
    emoji: "👥"
  },
];

const VALUES = [
  {
    icon: Shield,
    title: "Sicherheit",
    description: "Deine Daten sind bei uns sicher. Du bestimmst, was du teilen möchtest.",
    color: "#A0E8AF"
  },
  {
    icon: Heart,
    title: "Vertrauen",
    description: "Wir nehmen jede Meldung ernst und behandeln sie vertraulich.",
    color: "#FFC4D6"
  },
  {
    icon: Users,
    title: "Gemeinschaft",
    description: "Niemand sollte Mobbing alleine durchstehen müssen.",
    color: "#BDE0FE"
  },
  {
    icon: Target,
    title: "Handeln",
    description: "Wir wollen nicht nur zuhören, sondern auch etwas verändern.",
    color: "#F6E05E"
  },
];

const PARTNERS = [
  { name: "klicksafe.de", url: "https://www.klicksafe.de", description: "EU-Initiative für mehr Sicherheit im Netz" },
  { name: "Nummer gegen Kummer", url: "https://www.nummergegenkummer.de", description: "Beratung für Kinder, Jugendliche und Eltern" },
  { name: "juuuport.de", url: "https://www.juuuport.de", description: "Online-Beratung von Jugendlichen für Jugendliche" },
];

export default function UeberUns() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Ueber uns – Unsere Mission gegen Mobbing" description="Wer steckt hinter mobbing-melden.de? Unsere Mission, Werte und das Team. Gemeinsam gegen Mobbing und Cybermobbing in Deutschland." path="/ueber-uns" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#A0E8AF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-[#1A4731]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Über uns
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Wir sind Menschen, die glauben, dass kein Kind Mobbing erleben sollte. 
              Deshalb haben wir diese Plattform geschaffen.
            </p>
          </div>

          {/* Mission */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-3xl p-8 md:p-12 mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4 text-center" style={{ fontFamily: 'Nunito' }}>
              Unsere Mission 🎯
            </h2>
            <p className="text-lg text-[#718096] text-center max-w-2xl mx-auto leading-relaxed">
              Wir möchten Kindern und Jugendlichen eine <strong>sichere Anlaufstelle</strong> bieten, 
              um Mobbing zu melden. Wir glauben daran, dass gemeinsam mehr möglich ist - 
              und dass jedes Kind das Recht hat, ohne Angst aufzuwachsen.
            </p>
          </div>

          {/* Values */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6 text-center" style={{ fontFamily: 'Nunito' }}>
            Unsere Werte
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {VALUES.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${value.color}30` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: value.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2D3748] mb-1">{value.title}</h3>
                      <p className="text-[#718096]">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Team */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6 text-center" style={{ fontFamily: 'Nunito' }}>
            Wer steckt dahinter?
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-soft border border-gray-100 mb-12 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-bold text-[#2D3748] mb-2">Ehrenamtliches Team</h3>
            <p className="text-[#718096] max-w-xl mx-auto">
              Hinter mobbing-melden.de steht ein kleines Team aus engagierten Menschen, 
              die ehrenamtlich arbeiten. Wir sind keine große Organisation - 
              aber wir haben ein großes Herz für alle, die Hilfe brauchen.
            </p>
          </div>

          {/* Partners */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6 text-center" style={{ fontFamily: 'Nunito' }}>
            Partner & Empfehlungen
          </h2>
          <div className="space-y-4 mb-12">
            {PARTNERS.map((partner, index) => (
              <a 
                key={index}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover hover:-translate-y-1 transition-all"
              >
                <div className="w-12 h-12 bg-[#BDE0FE]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-[#1A365D]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#2D3748]">{partner.name}</h3>
                  <p className="text-sm text-[#718096]">{partner.description}</p>
                </div>
              </a>
            ))}
          </div>

          {/* Contact */}
          <div className="bg-[#A0E8AF]/10 rounded-2xl p-8 text-center">
            <Mail className="w-8 h-8 text-[#1A4731] mx-auto mb-3" />
            <h3 className="font-bold text-[#1A4731] mb-2">Kontakt</h3>
            <p className="text-[#718096] mb-4">
              Hast du Fragen zu uns oder möchtest du mitmachen?
            </p>
            <a 
              href="mailto:kontakt@mobbing-melden.de"
              className="text-[#1A4731] font-medium hover:underline"
            >
              kontakt@mobbing-melden.de
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
